import { useEffect, useRef, useState } from 'react';

/**
 * The chart kit's shared geometry and palette, moved in with the Admin module.
 *
 * No chart library. This is a hand-built SVG kit, kept because it is small
 * enough to read and because the mark rules the design system asks for — 2px
 * lines, 4px rounded data-ends anchored to the baseline, a 2px surface gap
 * between stacked segments — are easier to hold exactly here than to talk a
 * library out of.
 *
 * THE ONE TRAP, and it bit the app this came from. A colour reaching SVG must go
 * through `style`, never a presentation attribute: `fill="var(--series-1)"` does
 * not resolve — var() is not valid in a presentation attribute — and the browser
 * falls back to black in both themes. The standalone app painted every bar black
 * for exactly this reason. RoseChart.tsx documents the same trap and solves it
 * with CSS classes; the kit here has to use `style` instead, because its colours
 * are chosen per call site rather than fixed.
 *
 * So: `style={{ fill: s.color }}`, not `fill={s.color}`. Every component in this
 * folder follows that, and it is the first thing to check if a chart goes black.
 */

export interface Series {
  key: string;
  label: string;
  /** A CSS custom property reference, e.g. `var(--series-1)`. Applied through
   *  `style`, never as an attribute — see the note above. */
  color: string;
}

/** Measure a container so charts render at real pixel size rather than being
 *  stretched by a viewBox, which would scale the stroke widths and text with it. */
export function useMeasure<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0;
      /* Sub-pixel churn would re-render on every scroll in some browsers. */
      setWidth((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    });
    observer.observe(node);
    setWidth(node.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
}

/** "Nice" axis ticks — a rounded step, so the labels read 0/25/50/75 rather
 *  than 0/23.4/46.8. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) return [min === 0 ? 0 : min * 0.9, min, min === 0 ? 1 : min * 1.1];

  const raw = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm >= 7.5 ? 10 : norm >= 3.5 ? 5 : norm >= 1.5 ? 2 : 1) * mag;

  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  /* toFixed(10) then back, or floating-point accumulation produces ticks
     labelled 0.30000000000000004. */
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return ticks;
}

/**
 * Path for a bar with only its data-end rounded — the baseline end stays square
 * so bars sit flush on the axis rather than floating above it.
 */
export function barPath(
  x: number,
  y: number,
  w: number,
  h: number,
  side: 'top' | 'right' | 'bottom' | 'left' = 'top',
  radius = 4,
) {
  /* Clamped to half the bar, or a short bar's corners cross over and the path
     turns inside out. */
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));
  if (r === 0 || w <= 0 || h <= 0) {
    return `M${x},${y}h${Math.max(w, 0)}v${Math.max(h, 0)}h${-Math.max(w, 0)}Z`;
  }
  switch (side) {
    case 'right':
      return `M${x},${y} H${x + w - r} A${r},${r} 0 0 1 ${x + w},${y + r} V${y + h - r} A${r},${r} 0 0 1 ${x + w - r},${y + h} H${x} Z`;
    case 'left':
      return `M${x + w},${y} H${x + r} A${r},${r} 0 0 0 ${x},${y + r} V${y + h - r} A${r},${r} 0 0 0 ${x + r},${y + h} H${x + w} Z`;
    case 'bottom':
      return `M${x},${y} H${x + w} V${y + h - r} A${r},${r} 0 0 1 ${x + w - r},${y + h} H${x + r} A${r},${r} 0 0 1 ${x},${y + h - r} Z`;
    default:
      return `M${x},${y + h} V${y + r} A${r},${r} 0 0 1 ${x + r},${y} H${x + w - r} A${r},${r} 0 0 1 ${x + w},${y + r} V${y + h} Z`;
  }
}

/** A straight polyline path. Deliberately not smoothed: a spline through
 *  monthly points invents values between them that were never measured. */
export function linePath(points: Array<[number, number]>) {
  return points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
}

export function areaPath(points: Array<[number, number]>, baseline: number) {
  if (!points.length) return '';
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return '';
  return `${linePath(points)} L${last[0].toFixed(2)},${baseline} L${first[0].toFixed(2)},${baseline} Z`;
}

/**
 * The categorical slots, in fixed order — never cycled through at random, so
 * the same series is the same colour on every chart on the page.
 *
 * Six, not eight. The brand's crimson half is reserved for status and for
 * accents, which leaves the navy ramp, and it has about six distinguishable
 * steps — see the long note in styles/tokens.css. `seriesColor` folds past the
 * end rather than returning undefined, but a chart reaching slot seven wants a
 * different form, not another colour.
 */
export const SERIES_COLORS = Array.from({ length: 6 }, (_, i) => `var(--series-${i + 1})`);
export const seriesColor = (index: number) =>
  SERIES_COLORS[((index % SERIES_COLORS.length) + SERIES_COLORS.length) % SERIES_COLORS.length] as string;

/** The sequential ramp, for magnitude rather than identity. Ordered light to
 *  dark in the light theme and dark to light in the dark one, so "more" always
 *  reads as further from the page colour. */
export const SEQUENTIAL = [
  'var(--seq-250)',
  'var(--seq-350)',
  'var(--seq-450)',
  'var(--seq-550)',
  'var(--seq-650)',
];

/** Picks a sequential step for position `index` of `count`, so a five-step ramp
 *  spreads across however many rows there are instead of clustering at one end. */
export function sequentialColor(index: number, count: number): string {
  if (count <= 1) return SEQUENTIAL[SEQUENTIAL.length - 1] as string;
  const step = Math.round((index / (count - 1)) * (SEQUENTIAL.length - 1));
  return SEQUENTIAL[Math.min(Math.max(step, 0), SEQUENTIAL.length - 1)] as string;
}

/**
 * The status ladder, for charts plotting severity rather than category.
 *
 * Separate from the series slots on purpose: this is the one place a chart may
 * use a crimson step, because here the colour IS the meaning. Everything using
 * it also carries a text label, so nothing depends on colour alone.
 */
export const STATUS_COLOR = {
  good: 'var(--status-good)',
  warning: 'var(--status-warning)',
  serious: 'var(--status-serious)',
  critical: 'var(--status-critical)',
  neutral: 'var(--status-neutral)',
} as const;
